import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectInput,
  TextInput,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';

import type { HolidayEntry } from 'api/types/attendanceSettingsTypes';
import type { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';

interface AddHolidayModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAdd: (holiday: HolidayEntry) => void;
  initialData?: HolidayEntry | null;
  isPredefined?: boolean;
}

export const AddHolidayModal = ({
  isOpen,
  onDismiss,
  onAdd,
  initialData,
  isPredefined = false,
}: AddHolidayModalProps) => {
  const { formData } = useFormWithProvider<AttendanceSettingsFormState>();

  // Use locations from API if available, fallback to KNOWN_STATES.
  const availableLocations = formData.holidayLocations?.length
    ? formData.holidayLocations.map((l) => l.id)
    : [];

  const ALL_OPTION = '0';
  const stateLocations = availableLocations.filter((l) => l !== ALL_OPTION);
  const isEditMode = initialData != null;

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [selectedStates, setSelectedStates] = useState<string[]>([...availableLocations]);

  useEffect(() => {
    if (isOpen && initialData) {
      const [year, month, day] = initialData.date.split('-').map(Number);
      setDate(new Date(year, month - 1, day));
      setDescription(initialData.description);
      const locations =
        initialData.selectedLocations.length === 0
          ? []
          : initialData.selectedLocations.filter((location) => location !== ALL_OPTION);
      const isAll = locations.length === stateLocations.length;
      setSelectedStates(isAll ? [ALL_OPTION, ...locations] : locations);
    } else if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const actualStates = selectedStates.filter((s) => s !== ALL_OPTION);

  const canSubmit = date !== undefined && description.trim() !== '';

  const handleStatesChange = (values: string[]) => {
    const prevHadAll = selectedStates.includes(ALL_OPTION);
    const newHasAll = values.includes(ALL_OPTION);

    if (!prevHadAll && newHasAll) {
      // "All" was just selected → select everything
      setSelectedStates([ALL_OPTION, ...stateLocations]);
    } else if (prevHadAll && !newHasAll) {
      // "All" was just deselected → clear everything
      setSelectedStates([]);
    } else if (prevHadAll && newHasAll) {
      // "All" still present but a state was deselected → remove "All"
      setSelectedStates(values.filter((s) => s !== ALL_OPTION));
    } else {
      // Individual selection — auto-check "All" if all states are now selected
      const statesOnly = values.filter((s) => s !== ALL_OPTION);
      if (statesOnly.length && statesOnly.length === stateLocations.length) {
        setSelectedStates([ALL_OPTION, ...statesOnly]);
      } else {
        setSelectedStates(statesOnly);
      }
    }
  };

  const resetForm = () => {
    setDate(undefined);
    setDescription('');
    setSelectedStates([]);
  };

  const handleDismiss = () => {
    resetForm();
    onDismiss();
  };

  const handleSubmit = () => {
    if (!canSubmit || !date) return;
    const isoDate = getFormattedDateValue({
      date,
      formatString: dateFormats.isoDate,
    });

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    onAdd({
      id: initialData?.id ?? Date.now(),
      date: isoDate,
      day: dayNames[date.getDay()],
      description: description.trim(),
      isCustom: isPredefined ? false : true,
      selectedLocations: [...selectedStates],
    });

    resetForm();
  };

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} size="small">
      <ModalHeader
        title={
          isPredefined ? 'Edit applicable states' : isEditMode ? 'Edit holiday' : 'Add holiday'
        }
      />
      <ModalBody>
        <Box display="flex" flexDirection="column" gap="spacing.5">
          <DatePicker
            label="Date"
            selectionType="single"
            value={date ?? null}
            isDisabled={isPredefined}
            onChange={(value) => {
              setDate(value ?? undefined);
            }}
          />
          <TextInput
            label="Holiday name"
            placeholder="e.g. Diwali"
            value={description}
            isDisabled={isPredefined}
            onChange={({ value }) => setDescription(value ?? '')}
          />
          {formData.showLocations && (
            <Dropdown selectionType="multiple">
              <SelectInput
                label="Applies to"
                value={selectedStates}
                onChange={({ values }) => handleStatesChange(values ?? [])}
              />
              <DropdownOverlay>
                <ActionList>
                  {(formData.holidayLocations || [{ id: 0, name: 'All' }])?.map((state) => (
                    <ActionListItem key={state.id} title={state.name} value={state.id.toString()} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.4">
          <Button variant="secondary" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isDisabled={!canSubmit}>
            {isEditMode ? 'Save Changes' : 'Add Holiday'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
